import React, { useState, useEffect, Fragment } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Table, Button, Form, Row, Col } from "react-bootstrap";
import { Plus, Save } from "react-feather";

const LabEntry = ({ getLabItemsHandler, toggle, editData }) => {
  const auth = useSelector((state) => state.authentication);
  const lab = useSelector((state) => state.lab);

  const { labCategory } = lab;
  const { userData } = auth;

  const [errors, setErrors] = useState([]);
  const [category, setCategory] = useState([
    {
      categoryName: "",
      tests: [
        {
          testName: "",
          subTests: [
            {
              subTestName: "",
              specimen: "",
              referenceRange: "",
              units: "",
              method: "",
              notes: "",
            },
          ],
        },
      ],
      notes: "",
      createdBy: userData.user,
    },
  ]);

  useEffect(() => {
    if (editData) {
      setCategory([editData]);
    }
  }, [editData]);

  const handleInputChange = (
    categoryIndex,
    testIndex,
    subTestIndex,
    field,
    value
  ) => {
    const updatedCategory = [...category];
    updatedCategory[categoryIndex].tests[testIndex].subTests[subTestIndex][
      field
    ] = value;
    setCategory(updatedCategory);
  };

  const handleCategoryChange = (categoryIndex, field, value) => {
    const updatedCategory = [...category];
    updatedCategory[categoryIndex][field] = value;
    setCategory(updatedCategory);
  };

  const handleTestChange = (categoryIndex, testIndex, field, value) => {
    const updatedCategory = [...category];
    updatedCategory[categoryIndex].tests[testIndex][field] = value;
    setCategory(updatedCategory);
  };

  const addRow = () => {
    setCategory([
      ...category,
      {
        categoryName: "",
        tests: [
          {
            testName: "",
            subTests: [
              {
                subTestName: "",
                specimen: "",
                referenceRange: "",
                units: "",
                method: "",
                notes: "",
              },
            ],
          },
        ],
        createdBy: userData.user,
      },
    ]);
  };

  const validateCategory = () => {
    const newErrors = [];

    category.forEach((categoryItem, categoryIndex) => {
      const categoryErrors = {};

      if (!categoryItem.categoryName.trim()) {
        categoryErrors.categoryName = "Category name is required";
      }

      categoryItem.tests.forEach((test, testIndex) => {
        const testErrors = {};

        if (!test.testName.trim()) {
          testErrors.testName = "Test name is required";
        }

        test.subTests.forEach((subTest, subTestIndex) => {
          const subTestErrors = {};

          if (!subTest.subTestName.trim()) {
            subTestErrors.subTestName = "Sub-test name is required";
          }
          if (!subTest.specimen.trim()) {
            subTestErrors.specimen = "Specimen is required";
          }
          if (!subTest.referenceRange.trim()) {
            subTestErrors.referenceRange = "Reference range is required";
          }
          if (!subTest.units.trim()) {
            subTestErrors.units = "Units are required";
          }
          if (!subTest.method.trim()) {
            subTestErrors.method = "Method is required";
          }

          if (Object.keys(subTestErrors).length > 0) {
            if (!testErrors.subTests) testErrors.subTests = [];
            testErrors.subTests[subTestIndex] = subTestErrors;
          }
        });

        if (Object.keys(testErrors).length > 0) {
          if (!categoryErrors.tests) categoryErrors.tests = [];
          categoryErrors.tests[testIndex] = testErrors;
        }
      });

      if (Object.keys(categoryErrors).length > 0) {
        newErrors[categoryIndex] = categoryErrors;
      }
    });

    setErrors(newErrors);

    return newErrors.length === 0;
  };

  const submitHandler = () => {
    if (!validateCategory()) return;

    const apiUrl = editData
      ? `${process.env.REACT_APP_CRI_API}edit-lab`
      : `${process.env.REACT_APP_CRI_API}add-lab`;

    const requests = category.map((categoryItem) =>
      axios.post(apiUrl, categoryItem, {
        params: {
          branch: userData.branch,
          ...(editData && { id: categoryItem._id }),
        },
        headers: {
          Authorization: `Bearer ${userData.token}`,
        },
      })
    );

    axios
      .all(requests)
      .then((responses) => {
        responses.forEach(() => {
          getLabItemsHandler();
          toggle();
        });
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  return (
    <div className="bg-white p-2 rounded">
      
      {/* High Density Integrated Row Span Form Data Matrix */}
      <div className="table-responsive rounded-3 border border-light-subtle shadow-sm mb-4">
        <Table responsive bordered hover className="mb-0 align-middle text-nowrap">
          <thead className="table-dark text-nowrap small text-uppercase font-monospace border-bottom">
            <tr>
              <th className="py-3 px-3" style={{ width: "15%" }}>Category Name</th>
              <th className="py-3" style={{ width: "17%" }}>Test Name</th>
              <th className="py-3" style={{ width: "15%" }}>Sub Test Name</th>
              <th className="py-3" style={{ width: "10%" }}>Specimen</th>
              <th className="py-3" style={{ width: "12%" }}>Reference Range</th>
              <th className="py-3" style={{ width: "10%" }}>Units</th>
              <th className="py-3" style={{ width: "11%" }}>Method</th>
              <th className="py-3" style={{ width: "15%" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {category.map((cat, categoryIndex) => (
              <Fragment key={cat._id || categoryIndex}>
                {cat.tests.map((test, testIndex) => (
                  <Fragment key={testIndex}>
                    {test.subTests.map((subTest, subTestIndex) => (
                      <tr key={subTestIndex} className="align-middle">
                        
                        {/* Normalized Row Span Category Picker Dropdown Cell */}
                        {subTestIndex === 0 && (
                          <td rowSpan={test.subTests.length} className="bg-light bg-opacity-25 p-2 align-top" style={{ minWidth: "160px" }}>
                            <Form.Select
                              size="sm"
                              value={cat.categoryName}
                              className="border-light-subtle shadow-xs py-1.5 fw-medium"
                              onChange={(e) =>
                                handleCategoryChange(
                                  categoryIndex,
                                  "categoryName",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Select a category</option>
                              {labCategory && labCategory.map((labCat) => (
                                <option
                                  key={labCat._id}
                                  value={labCat.categoryName}
                                >
                                  {labCat.categoryName}
                                </option>
                              ))}
                            </Form.Select>
                            {errors[categoryIndex]?.categoryName && (
                              <div className="text-danger small mt-1 fw-semibold px-1">
                                {errors[categoryIndex].categoryName}
                              </div>
                            )}
                          </td>
                        )}
                        
                        {/* Normalized Row Span Test Parameter Identity Cell */}
                        {subTestIndex === 0 && (
                          <td rowSpan={test.subTests.length} className="bg-light bg-opacity-10 p-2 align-top" style={{ minWidth: "180px" }}>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={test.testName}
                              className="border-light-subtle shadow-xs py-1.5 fw-medium"
                              placeholder="Test Identifier"
                              onChange={(e) =>
                                handleTestChange(
                                  categoryIndex,
                                  testIndex,
                                  "testName",
                                  e.target.value
                                )
                              }
                            />
                            {errors[categoryIndex]?.tests?.[testIndex]?.testName && (
                              <div className="text-danger small mt-1 fw-semibold px-1">
                                {errors[categoryIndex].tests[testIndex].testName}
                              </div>
                            )}
                          </td>
                        )}

                        {/* Leaf Row Parameters Mapping Fields */}
                        <td className="p-2">
                          <Form.Control
                            type="text"
                            size="sm"
                            value={subTest.subTestName}
                            className="border-light-subtle shadow-xs py-1.5"
                            placeholder="Sub-test description"
                            onChange={(e) =>
                              handleInputChange(
                                categoryIndex,
                                testIndex,
                                subTestIndex,
                                "subTestName",
                                e.target.value
                              )
                            }
                          />
                          {errors[categoryIndex]?.tests?.[testIndex]?.subTests?.[subTestIndex]?.subTestName && (
                            <div className="text-danger small mt-1 fw-semibold px-1">
                              {errors[categoryIndex].tests[testIndex].subTests[subTestIndex].subTestName}
                            </div>
                          )}
                        </td>

                        <td className="p-2">
                          <Form.Control
                            type="text"
                            size="sm"
                            value={subTest.specimen}
                            className="border-light-subtle shadow-xs py-1.5"
                            placeholder="e.g., Serum"
                            onChange={(e) =>
                              handleInputChange(
                                categoryIndex,
                                testIndex,
                                subTestIndex,
                                "specimen",
                                e.target.value
                              )
                            }
                          />
                          {errors[categoryIndex]?.tests?.[testIndex]?.subTests?.[subTestIndex]?.specimen && (
                            <div className="text-danger small mt-1 fw-semibold px-1">
                              {errors[categoryIndex].tests[testIndex].subTests[subTestIndex].specimen}
                            </div>
                          )}
                        </td>

                        <td className="p-2">
                          <Form.Control
                            type="text"
                            size="sm"
                            value={subTest.referenceRange}
                            className="border-light-subtle shadow-xs font-monospace py-1.5"
                            placeholder="Normative bounds"
                            onChange={(e) =>
                              handleInputChange(
                                categoryIndex,
                                testIndex,
                                subTestIndex,
                                "referenceRange",
                                e.target.value
                              )
                            }
                          />
                          {errors[categoryIndex]?.tests?.[testIndex]?.subTests?.[subTestIndex]?.referenceRange && (
                            <div className="text-danger small mt-1 fw-semibold px-1">
                              {errors[categoryIndex].tests[testIndex].subTests[subTestIndex].referenceRange}
                            </div>
                          )}
                        </td>

                        <td className="p-2">
                          <Form.Control
                            type="text"
                            size="sm"
                            value={subTest.units}
                            className="border-light-subtle shadow-xs py-1.5"
                            placeholder="e.g., mg/dL"
                            onChange={(e) =>
                              handleInputChange(
                                categoryIndex,
                                testIndex,
                                subTestIndex,
                                "units",
                                e.target.value
                              )
                            }
                          />
                          {errors[categoryIndex]?.tests?.[testIndex]?.subTests?.[subTestIndex]?.units && (
                            <div className="text-danger small mt-1 fw-semibold px-1">
                              {errors[categoryIndex].tests[testIndex].subTests[subTestIndex].units}
                            </div>
                          )}
                        </td>

                        <td className="p-2">
                          <Form.Control
                            type="text"
                            size="sm"
                            value={subTest.method}
                            className="border-light-subtle shadow-xs py-1.5"
                            placeholder="e.g., ELISA"
                            onChange={(e) =>
                              handleInputChange(
                                categoryIndex,
                                testIndex,
                                subTestIndex,
                                "method",
                                e.target.value
                              )
                            }
                          />
                          {errors[categoryIndex]?.tests?.[testIndex]?.subTests?.[subTestIndex]?.method && (
                            <div className="text-danger small mt-1 fw-semibold px-1">
                              {errors[categoryIndex].tests[testIndex].subTests[subTestIndex].method}
                            </div>
                          )}
                        </td>

                        <td className="p-2">
                          <Form.Control
                            as="textarea"
                            rows={1}
                            value={subTest.notes}
                            className="border-light-subtle shadow-xs small text-muted py-1"
                            placeholder="Add evaluation remarks..."
                            style={{ minWidth: "180px", resize: "vertical" }}
                            onChange={(e) =>
                              handleInputChange(
                                categoryIndex,
                                testIndex,
                                subTestIndex,
                                "notes",
                                e.target.value
                              )
                            }
                          />
                        </td>

                      </tr>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Control Actions Row Panel */}
      <Row className="g-3 align-items-center border-top pt-3 mt-2">
        <Col className="d-flex gap-2">
          <Button
            variant="outline-primary"
            className="d-inline-flex align-items-center gap-1.5 px-3 py-2 shadow-sm bg-white"
            onClick={addRow}
          >
            <Plus size={16} /> Add Entry Row
          </Button>
          <Button
            variant="success"
            className="d-inline-flex align-items-center gap-1.5 px-4 py-2 shadow-sm ms-auto"
            onClick={submitHandler}
          >
            <Save size={16} /> Save Entries
          </Button>
        </Col>
      </Row>

    </div>
  );
};

export default LabEntry;